/**
 * MasterPatternViewCreateForm — standalone creation-only form for a MasterPatternView
 * master: no list, no toolbar, just the tabs/fields/save-cancel of the "create" flow.
 *
 * Self-contained (own QueryClient + isolated Zustand store via MasterViewProvider), so
 * it can be embedded anywhere without pulling in the full list+toolbar shell — e.g. as
 * a LookupField `onCreateNew`, letting a lookup field for another MasterPatternView-based
 * master become a full "entity control" with a single prop.
 *
 * @module components/MasterPatternViewCreateForm
 */
import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MasterViewProvider, useMasterViewStore } from '../store/MasterViewContext';
import { MasterPatternViewForm } from './form/MasterPatternViewForm';
import { useMasterForm } from '../hooks/useMasterForm';
import type { ToastCallback } from '../hooks/useToast';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import type { MasterPatternViewEntity, MasterPatternViewService, MasterPatternViewCompany } from '../types/service.types';
import type { MasterPatternViewPermissions } from '../types/permissions.types';

export interface MasterPatternViewCreateFormProps<T extends MasterPatternViewEntity> {
  /**
   * Returns the target master's definition — same shape its own <MasterPatternView>
   * uses. This is a FUNCTION (not a resolved value) so it can be a parameterless React
   * hook (e.g. `useAccountDefinition`) without forcing the caller to invoke it outside
   * of a component render. That matters specifically for a SELF-REFERENCING lookup
   * (e.g. Account's own "parent account" field, which points back at Account itself):
   * if the caller had to resolve the definition eagerly to build `create.useDefinition`,
   * building Account's own definition would require calling `useAccountDefinition()`
   * from inside `useAccountDefinition()` itself — direct, unconditional recursion that
   * crashes the very first time the form renders, not a rules-of-hooks nuance to work
   * around. Passing the function instead defers the call until THIS component actually
   * mounts, which only happens when the user opens the "+" popup — a fresh, independent
   * render, not a call nested inside the definition that's still being constructed.
   *
   * For a definition that's already a plain constant (no hook involved), just wrap it:
   * `useDefinition={() => productosDefinition}`.
   */
  useDefinition: () => MasterPatternViewDefinition;
  /** The target master's service — only `create` (and `getAll`, required by the type) matter here. */
  service: MasterPatternViewService<T>;
  /** Human-readable entity name shown in the form header. Falls back to `definition.name`. */
  entityName?: string;
  /** Called with the newly created record once `service.create` resolves. */
  onCreated: (record: T) => void;
  /** Called when the user cancels out of the form without creating anything. */
  onCancel: () => void;
  /** Pre-filled values merged into the fresh create form (same as MasterPatternView's own prop). */
  defaultCreateValues?: Record<string, unknown>;
  /** Custom synchronous validation, same contract as MasterPatternView's own `validate` prop. */
  validate?: (values: Partial<T>) => Record<string, string>;
  /** Permission configuration — gates the Editar/Guardar affordances same as the full component. */
  permissions?: MasterPatternViewPermissions;
  /**
   * External toast callback. Omit to keep this form silent (no InternalToastContainer is
   * mounted for this isolated instance, so the default internal toast has nowhere to render).
   */
  toast?: ToastCallback;
  /** Translation function override. */
  t?: (key: string, defaultValue?: string) => string;
  /**
   * Visual chrome wrapped around the form content.
   * - `'none'` (default) — no wrapper; the host fully controls the surrounding chrome
   *   (e.g. embedding the form inline inside its own page section).
   * - `'modal'` | `'sidebar'` — wraps the form in the exact same background/shadow/
   *   corner-radius classes MasterPatternView's own `ModalPanel`/`SidebarPanel` use, so
   *   a popup host (e.g. LookupField's `onCreateNew`) can present it looking identical
   *   to the master's real create modal/sidebar, without inventing its own card chrome
   *   that would just duplicate this one.
   * @default 'none'
   */
  chrome?: 'none' | 'modal' | 'sidebar';
  /**
   * Full company list, for a target master whose own definition is company-scoped
   * (`companyBehavior !== 'NONE'`). Same shape the target's own `<MasterPatternView>`
   * would receive. Omit for a company-agnostic master — the form then renders with no
   * company selector at all, same as before this prop existed.
   */
  companies?: MasterPatternViewCompany[];
  /**
   * Company this new record should be created under — typically the host's own
   * currently active/inherited company (e.g. a "Cuentas" lookup opened while editing
   * a record already scoped to "ACME SAS" should create its "Centro de Costo" under
   * ACME SAS too, not under "Global"). Preseeds the form's own isolated company
   * context on mount; without it the form falls back to `companies[0]`, same as a
   * fresh top-level create.
   */
  defaultCompany?: MasterPatternViewCompany;
  /**
   * Locks the company selector so the user can't change it away from `defaultCompany`.
   * Plain opt-in, `false` unless the caller says otherwise — this component is used
   * standalone too (not just behind a lookup's "+"), where a suggested default
   * shouldn't silently become mandatory. The lookup-create wiring in FieldRenderer.tsx
   * is the one call site that actually wants this on by default; it passes it
   * explicitly rather than relying on a default here. Has no effect without
   * `defaultCompany` (nothing to lock to).
   * @default false
   */
  companyLocked?: boolean;
}

// Same as the public props, minus `defaultCreateValues` (already consumed by
// MasterViewProvider in the wrapper below) and `useDefinition` (already resolved to a
// plain `definition` value by the wrapper — this inner component never calls hooks
// that produce a definition itself).
type MasterPatternViewCreateFormInnerProps<T extends MasterPatternViewEntity> = Omit<
  MasterPatternViewCreateFormProps<T>,
  'defaultCreateValues' | 'useDefinition'
> & {
  definition: MasterPatternViewDefinition;
};

function MasterPatternViewCreateFormInner<T extends MasterPatternViewEntity>({
  definition,
  service,
  entityName,
  onCreated,
  onCancel,
  validate,
  permissions,
  toast,
  t,
  chrome = 'none',
  companies,
  defaultCompany,
  companyLocked = false,
}: MasterPatternViewCreateFormInnerProps<T>) {
  const openCreate = useMasterViewStore((s) => s.openCreate);
  const setActiveCompanyContext = useMasterViewStore((s) => s.setActiveCompanyContext);
  useEffect(() => {
    openCreate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Seeds this form's own ISOLATED store with the host's currently active company —
  // without this it always starts undefined/'global' regardless of what the host was
  // already scoped to, which is what made the popup default to "Global" instead of
  // the company the record was actually about to be created under.
  //
  // Deliberately a SEPARATE effect keyed on `defaultCompany?.id` (not folded into the
  // mount-once effect above, and not left as `[]`): the host that opens this popup — a
  // LookupField's "+", specifically — commonly keeps this component's subtree mounted
  // between separate open/close cycles (e.g. for close-transition animations) rather than
  // fully unmounting it. A `[]`-effect only runs once for the component's entire mounted
  // lifetime, so it captures whatever company happened to be active the very FIRST time
  // the popup was ever opened and never re-applies it on a later re-open with a different
  // company — even though `defaultCompany` itself is a perfectly up-to-date prop on every
  // one of those renders. Keying on the id (not the object) avoids re-firing on every
  // parent render if `companies` is rebuilt with fresh-but-equivalent objects each time.
  useEffect(() => {
    if (defaultCompany) setActiveCompanyContext(defaultCompany.id);
  // Keyed on the id on purpose — see comment above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCompany?.id, setActiveCompanyContext]);

  const { submit } = useMasterForm<T>({
    definition,
    service,
    validate,
    onCreateSuccess: onCreated,
    toast,
    t,
  });

  const form = (
    <MasterPatternViewForm<T>
      definition={definition}
      entityName={entityName ?? definition.name}
      navigationType="inline"
      onSave={submit}
      onCancel={onCancel}
      onRequestRefresh={() => {}}
      permissions={permissions}
      t={t}
      companies={companies}
      onCompanyChange={(company) => setActiveCompanyContext(company.id)}
      companyDisabled={companyLocked && !!defaultCompany}
      // In 'none' chrome the host owns the surrounding layout (e.g. inline in its own
      // page section, which usually has other cues that content continues below) —
      // in 'modal'/'sidebar' this form is confined to a popup where a hidden
      // scrollbar is the only thing that would tell the user there's more to scroll to.
      showScrollbar={chrome !== 'none'}
    />
  );

  if (chrome === 'modal') {
    return (
      <div className="bg-background-secondary dark:bg-dark-bg-secondary rounded-lg shadow-2xl flex flex-col h-full overflow-hidden">
        {form}
      </div>
    );
  }

  if (chrome === 'sidebar') {
    return (
      <div className="bg-background-secondary dark:bg-dark-bg-secondary shadow-xl flex flex-col h-full overflow-hidden">
        {form}
      </div>
    );
  }

  return form;
}

export function MasterPatternViewCreateForm<T extends MasterPatternViewEntity>({
  useDefinition,
  defaultCreateValues,
  ...rest
}: MasterPatternViewCreateFormProps<T>) {
  // The one and only place `useDefinition` is invoked — this component's own render,
  // which only happens on a fresh mount when the popup opens. See the prop's doc
  // comment for why this must not be resolved any earlier than this.
  const definition = useDefinition();
  const [client] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={client}>
      <MasterViewProvider<T> definition={definition} defaultCreateValues={defaultCreateValues}>
        <MasterPatternViewCreateFormInner<T> definition={definition} {...rest} />
      </MasterViewProvider>
    </QueryClientProvider>
  );
}
